// Theory Coach - ChickyTutor-style proactive guidance for practice tests
// Specialized agent for step-by-step practice test guidance
// Supports OpenAI GPT-4o-mini for better quality
// Main class file - imports from split modules to prevent Cursor crashes

import { logger } from '../utils/logger';
import { unifiedState } from './unifiedStateManager';
// OpenAI calls now go through server proxy at /api/coach
import {
  getFollowUpHelper,
  getAnswerFirstPrompt,
  getThinkingPrompt,
  getProceedPrompt,
  getCorrectConfirmation,
  getProactiveFollowUpPrompt,
  getVariationPrompt,
  getMistakeRecapLabels,
  getKeyRuleLine,
  getMemoryHookLine,
  getReflectionPrompt,
  getClarifyPrompt,
  getReflectionSuggestions,
  getLastQuestionPrompt,
  getErrorPrompt,
  getInputPlaceholder,
  getDisclaimer,
  getNextStepText,
  getGreetingForNewUser,
  getGreetingForReturningUser,
  getGreetingCard,
  getContinueCard
} from './theoryCoach.prompts';
import {
  getFollowUpSuggestions,
  getReflectionPromptForQuestion,
  generateTestCompletionMessage
} from './theoryCoach.templates';
import {
  formatResponse,
  formatCoachReply,
  formatQuestionForVoice,
  formatExplanationForVoice,
  getWhyWrongText,
  getAnswerFirstLines,
  getConciseExplanationText
} from './theoryCoach.formatting';
import {
  getAnswerFeedback,
  answerUserQuestion,
  generateFollowUpSuggestions as generateFollowUpSuggestionsAI,
  generateCheckIn as generateCheckInAI
} from './theoryCoach.ai';
import {
  getFallbackResponse,
  shouldGuideToFocusArea,
  getTransitionMessage,
  guideToFocusArea
} from './theoryCoach.core';

// Export types
export type { TheoryCoachContext } from './theoryCoach.types';

class TheoryCoach {
  private openai: any = null;
  private useOpenAI: boolean = false;

  constructor() {
    // Initialize OpenAI if available and configured
    const hasProxy = !!process.env.REACT_APP_API_URL;
    if (hasProxy) {
      this.useOpenAI = false;
      logger.info('Theory Coach using server proxy at /api/coach');
    } else {
      logger.warn('⚠️ API proxy URL not configured. Set REACT_APP_API_URL in .env file.');
    }
  }

  // --- Consistent UI/voice copy helpers (ChickyTutor-style) ---
  getFollowUpHelper = getFollowUpHelper;
  getAnswerFirstPrompt = getAnswerFirstPrompt;
  getThinkingPrompt = getThinkingPrompt;
  getProceedPrompt = getProceedPrompt;
  getCorrectConfirmation = getCorrectConfirmation;
  getProactiveFollowUpPrompt = getProactiveFollowUpPrompt;
  getVariationPrompt = getVariationPrompt;
  getMistakeRecapLabels = getMistakeRecapLabels;
  getKeyRuleLine = getKeyRuleLine;
  getMemoryHookLine = getMemoryHookLine;
  // PROMPT MATCH: Greeting helpers
  getGreetingForNewUser = getGreetingForNewUser;
  getGreetingForReturningUser = getGreetingForReturningUser;
  getGreetingCard = getGreetingCard;
  getContinueCard = getContinueCard;
  getReflectionPrompt = getReflectionPrompt;
  getClarifyPrompt = getClarifyPrompt;
  getReflectionSuggestions = getReflectionSuggestions;
  getLastQuestionPrompt = getLastQuestionPrompt;
  getErrorPrompt = getErrorPrompt;
  getInputPlaceholder = getInputPlaceholder;
  getDisclaimer = getDisclaimer;
  getNextStepText = getNextStepText;

  // Template methods
  getFollowUpSuggestions = getFollowUpSuggestions;
  getReflectionPromptForQuestion = getReflectionPromptForQuestion;
  generateTestCompletionMessage = generateTestCompletionMessage;

  // Formatting methods
  formatCoachReply = formatCoachReply;
  formatQuestionForVoice = formatQuestionForVoice;
  formatExplanationForVoice = formatExplanationForVoice;
  getWhyWrongText = getWhyWrongText;
  getAnswerFirstLines = getAnswerFirstLines;
  getConciseExplanationText = getConciseExplanationText;

  // AI methods
  async generateFollowUpSuggestions(params: {
    language: string;
    questionText?: string;
    subject?: string;
    hasImage?: boolean;
    correctAnswer?: string;
    explanation?: string;
    userLastMessage?: string;
    coachLastResponse?: string;
    conversationHistory?: Array<{ role: 'user' | 'coach'; content: string }>;
    isCorrect?: boolean; // PROMPT MATCH: Needed for adaptive follow-up logic
    roundNumber?: number; // PROMPT MATCH: Round 1 (reasoning), Round 2 (misconception), Round 3 (reinforcement)
    lockedMisconceptionId?: string | null; // STRICT: Locked misconception ID - all cards must address ONLY this misconception
    questionId?: string; // Question ID for misconception mapping
    wrongOptions?: Array<{ id: string; text: string }>; // Wrong options for misconception mapping
  }): Promise<string[]> {
    return generateFollowUpSuggestionsAI(
      this.openai,
      this.useOpenAI,
      params
    );
  }

  async generateCheckIn(
    focusArea: any | null,
    recentMistakes: number,
    mistakesInTopic: number,
    currentTopic: string,
    language: string
  ): Promise<string> {
    return generateCheckInAI(
      this.openai,
      this.useOpenAI,
      focusArea,
      recentMistakes,
      mistakesInTopic,
      currentTopic,
      language
    );
  }

  /**
   * Generate response based on context
   */
  async generateResponse(context: any): Promise<string> {
    try {
      // Pattern 1: After answer (correct/incorrect) - CONDITIONAL PROACTIVE
      if (context.isCorrect !== undefined && context.currentQuestion) {
        return await getAnswerFeedback(this.openai, this.useOpenAI, context);
      }

      // Pattern 2: User asks a question
      if (context.userMessage && context.userMessage.trim()) {
        return await answerUserQuestion(this.openai, this.useOpenAI, context);
      }

      // Pattern 3: Transition between questions - MINIMAL (only when useful)
      if (context.questionNumber && context.questionNumber > 1 && context.previousCorrect !== undefined) {
        return getTransitionMessage(context);
      }

      // Pattern 4: Focus area guidance (between questions)
      const focusAreas = unifiedState.getFocusAreas();
      if (focusAreas.length > 0 && shouldGuideToFocusArea(context)) {
        return guideToFocusArea(focusAreas[0], context);
      }

      // Default: Stay silent unless explicitly triggered
      return '';
    } catch (error) {
      logger.error('Theory Coach error:', error);
      return getFallbackResponse(context);
    }
  }

  /**
   * Check if should give proactive feedback (REFINED: Only when needed)
   * Returns true if:
   * - User gets 2 wrong answers in a row, OR
   * - The question relates to a detected focus area
   */
  shouldGiveProactiveFeedback(
    isCorrect: boolean,
    wrongAnswersInRow: number,
    currentQuestionTopic: string,
    focusAreas: any[]
  ): boolean {
    // Give feedback if 2 wrong in a row
    if (!isCorrect && wrongAnswersInRow >= 2) {
      return true;
    }

    // Give feedback if question relates to focus area
    if (focusAreas.length > 0) {
      const currentTopic = currentQuestionTopic.toLowerCase();
      const hasFocusAreaMatch = focusAreas.some(area => {
        const areaName = (area.testName || area.testId || '').toLowerCase();
        return currentTopic.includes(areaName) || areaName.includes(currentTopic);
      });
      if (hasFocusAreaMatch) {
        return true;
      }
    }

    // Otherwise, no proactive feedback
    return false;
  }

  /**
   * Check if should check in (REFINED: Only when struggling)
   * Returns true if:
   * - User is struggling in a focus area (3+ mistakes in focus area topic), OR
   * - They make repeated mistakes in any topic (5+ mistakes overall)
   */
  shouldCheckIn(
    questionNumber: number,
    recentMistakes: number,
    recentMistakesInTopic: number,
    currentQuestionTopic: string,
    focusAreas: any[],
    lastCheckInTime: number = 0
  ): boolean {
    // Don't check in too early (need at least 3 questions)
    if (questionNumber < 3) {
      return false;
    }

    // Cooldown: 30 seconds minimum between check-ins
    const now = Date.now();
    if (now - lastCheckInTime < 30000) {
      return false;
    }

    // Priority 1: Check in if struggling in focus area (3+ mistakes)
    if (focusAreas.length > 0 && recentMistakesInTopic >= 3) {
      const currentTopic = currentQuestionTopic.toLowerCase();
      const hasFocusAreaMatch = focusAreas.some(area => {
        const areaName = (area.testName || area.testId || '').toLowerCase();
        return currentTopic.includes(areaName) || areaName.includes(currentTopic);
      });
      if (hasFocusAreaMatch) {
        return true;
      }
    }

    // Priority 2: Check in if repeated mistakes (5+ mistakes overall)
    if (recentMistakes >= 5) {
      return true;
    }

    // Otherwise, no check-in
    return false;
  }

  /**
   * Build trap-focused reply (requires formatResponse, so kept in main class)
   */
  buildTrapFocusedReply(params: {
    language: string;
    ruleSnippet: string;
    whyWrong?: string;
    userMessage?: string;
  }): string {
    const { language, ruleSnippet, whyWrong, userMessage } = params;
    const userMsg = (userMessage || '').toLowerCase().trim();
    const ruleLine = ruleSnippet ? `${ruleSnippet}` : '';
    
    // Context-aware responses based on user message
    if (userMsg.includes('misread') || userMsg.includes('misread the light') || userMsg.includes('misread the sign')) {
      if (whyWrong && ruleLine) {
        if (language === 'nl') {
          return formatResponse(`Je las het verkeerslicht verkeerd. ${whyWrong} Regel: ${ruleLine}.`, language);
        }
        if (language === 'ar') {
          return formatResponse(`قرأت إشارة المرور بشكل خاطئ. ${whyWrong} القاعدة: ${ruleLine}.`, language);
        }
        return formatResponse(`You misread the traffic light. ${whyWrong} Rule: ${ruleLine}.`, language);
      }
      if (ruleLine) {
        if (language === 'nl') {
          return formatResponse(`Kijk naar de details van het verkeerslicht. Regel: ${ruleLine}.`, language);
        }
        if (language === 'ar') {
          return formatResponse(`انظر إلى تفاصيل إشارة المرور. القاعدة: ${ruleLine}.`, language);
        }
        return formatResponse(`Look at the traffic light details. Rule: ${ruleLine}.`, language);
      }
    }
    
    if (userMsg.includes('guessed') || userMsg.includes('guess')) {
      if (ruleLine) {
        if (language === 'nl') {
          return formatResponse(`De regel hier is: ${ruleLine}. Onthoud dit voor volgende keer.`, language);
        }
        if (language === 'ar') {
          return formatResponse(`القاعدة هنا هي: ${ruleLine}. تذكر هذا للمرة القادمة.`, language);
        }
        return formatResponse(`The rule here is: ${ruleLine}. Remember this for next time.`, language);
      }
    }
    
    // Default trap-focused reply - avoid repeating rule if it's already in explanation
    if (whyWrong && ruleLine) {
      // Only include rule if whyWrong doesn't already contain similar information
      const whyWrongLower = whyWrong.toLowerCase();
      const ruleLower = ruleLine.toLowerCase();
      const ruleAlreadyMentioned = whyWrongLower.includes(ruleLower.substring(0, 20)) || 
                                    ruleLower.length < 30; // Short rules are likely already covered
      
      if (ruleAlreadyMentioned) {
        // Rule already covered in whyWrong, don't repeat it
        if (language === 'nl') {
          return formatResponse(`Dat is de valkuil: ${whyWrong}.`, language);
        }
        if (language === 'ar') {
          return formatResponse(`هذه هي الفخ: ${whyWrong}.`, language);
        }
        return formatResponse(`That's the trap: ${whyWrong}.`, language);
      } else {
        // Rule not mentioned, include it
        if (language === 'nl') {
          return formatResponse(`Dat is de valkuil: ${whyWrong}. Regel: ${ruleLine}.`, language);
        }
        if (language === 'ar') {
          return formatResponse(`هذه هي الفخ: ${whyWrong}. القاعدة: ${ruleLine}.`, language);
        }
        return formatResponse(`That's the trap: ${whyWrong}. Rule: ${ruleLine}.`, language);
      }
    }
    // Only show rule if it's not already in the explanation context
    if (ruleLine && ruleLine.length > 30) { // Only for longer rules that might add value
      if (language === 'nl') return formatResponse(`Regel: ${ruleLine}.`, language);
      if (language === 'ar') return formatResponse(`القاعدة: ${ruleLine}.`, language);
      return formatResponse(`Rule: ${ruleLine}.`, language);
    }
    return getClarifyPrompt(language);
  }
}

export const theoryCoach = new TheoryCoach();
